import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { protectSheetWithRandomPassword } from "../ExcelUtils";

const LoanSummaryForDayExcel = async (
  data,
  headerStyle,
  subtotalStyle,
  formattedDate,
  loginPlaceId,
  title = "Loan Dispose and Outstanding Summary"
) => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Loan Summary");
  const amountFormat = "#,##0.00";
  const percentFormat = "0.00%";

  // ---- Title Row ----
  const totalColumns = 1 + data.reduce((sum, district) => {
    return sum + district.loanSummaryWithDivisionalDtoList.reduce((dsum, div) => dsum + div.loanSummaryWithBranchDtoList.length * 12, 0);
  }, 0);

  const titleRow = sheet.addRow([]);
  sheet.mergeCells(1, 2, 1, totalColumns);
  const titleCell = sheet.getCell(1, 2);
  titleCell.value = title;
  titleCell.font = { bold: true, size: 16 };
  titleRow.height = 25;
  // ---- Printed Date ----
  const dateRow = sheet.addRow([` ${formattedDate}`]);
  sheet.mergeCells(2, 1, 2, totalColumns);
  dateRow.alignment = { horizontal: "left" };
  dateRow.font = { italic: true, size: 11, color: { argb: "FF555555" } };

  // ---- District / Divisional / Branch Headers ----
  const rowDistrict = sheet.getRow(3);
  const rowDivisional = sheet.getRow(4);
  const rowBranch = sheet.getRow(5);

  let colIndex = 2; // Start from column B (A is Product Name)
  data.forEach(district => {
    const districtStartCol = colIndex;
    let districtColSpan = 0;

    district.loanSummaryWithDivisionalDtoList.forEach(divisional => {
      const divisionalStartCol = colIndex;
      const divisionalColSpan = divisional.loanSummaryWithBranchDtoList.length * 4;

      // Divisional Header
      sheet.mergeCells(4, divisionalStartCol, 4, divisionalStartCol + divisionalColSpan - 1);
      const divisionalCell = sheet.getRow(4).getCell(divisionalStartCol);
      divisionalCell.value = divisional.divisionalName;
      if (headerStyle) divisionalCell.style = headerStyle;
      divisionalCell.alignment = { horizontal: "center" };

      // Branch Headers
      divisional.loanSummaryWithBranchDtoList.forEach(branch => {
        sheet.mergeCells(5, colIndex, 5, colIndex + 3);
        const branchCell = sheet.getRow(5).getCell(colIndex);
        branchCell.value = branch.branchName;
        if (headerStyle) branchCell.style = headerStyle;
        branchCell.alignment = { horizontal: "center" };
        colIndex += 4;
      });

      districtColSpan += divisionalColSpan;
    });

    // District Header
    sheet.mergeCells(3, districtStartCol, 3, districtStartCol + districtColSpan - 1);
    const districtCell = sheet.getRow(3).getCell(districtStartCol);
    districtCell.value = district.districtName;
    if (headerStyle) districtCell.style = headerStyle;
    districtCell.alignment = { horizontal: "center" };
  });

  // ---- Column Names ----
  const columnNames = ["Product Name"];
  const allBranches = [];
  data.forEach(district => {
    district.loanSummaryWithDivisionalDtoList.forEach(divisional => {
      divisional.loanSummaryWithBranchDtoList.forEach(branch => {
        allBranches.push(branch);
        columnNames.push(
          "Disbursement Count",
          "Disbursement Amount",
          "Outstanding Count",
          "Outstanding Amount",
        );
      });
    });
  });
  const headerRow = sheet.addRow(columnNames);
  headerRow.eachCell(cell => {
    if (headerStyle) cell.style = headerStyle;
    cell.alignment = { horizontal: "center" };
  });

  // ---- Data Rows ----
  const productNames = Array.from(
    new Set(allBranches.flatMap(branch => branch.loanSummaryDtoList.map(p => p.productName)))
  );

  productNames.forEach((productName, rowIndex) => {
    const rowValues = [productName];
    const dataRowIndex = sheet.lastRow.number + 1;

    allBranches.forEach(branch => {
      const p = branch.loanSummaryDtoList.find(p => p.productName === productName);
      if (p) {
        rowValues.push(
          p.disbursementCount,
          p.disbursementAmount,
          p.outStandingCount,
          p.outStandingAmount,
        );
      } else {
        rowValues.push(...Array(4).fill(null));
      }
    });

    const dataRow = sheet.addRow(rowValues);


    // Zebra striping
    if (rowIndex % 2 === 1) {
      dataRow.eachCell(cell => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFDCDCDC" },
        };
      });
    }

    // Amount formatting
    let amountColBase = 2;
    allBranches.forEach(() => {
      const amountCols = [amountColBase + 1, amountColBase + 3];
      amountCols.forEach(c => {
        const cell = dataRow.getCell(c);
        if (cell.value !== null && cell.value !== undefined) cell.numFmt = amountFormat;
      });
      amountColBase += 4;
    });
  });

  // ---- Subtotal Row ----
  const subtotalRowValues = ["Total"];
  let colStart = 2;
  allBranches.forEach(() => {
    const colIndexes = Array.from({ length: 4 }, (_, i) => colStart + i);

    const disbAmtCol = colIndexes[1];
    const outStandingAmtCol = colIndexes[3];

    const startRow = 6; // First data row
    const endRow = sheet.lastRow.number;

    colIndexes.forEach(col => {
      const letter = sheet.getColumn(col).letter;

      subtotalRowValues[col - 1] = { formula: `SUM(${letter}${startRow}:${letter}${endRow})` };

    });

    colStart += 4;
  });

  const subtotalRow = sheet.addRow(subtotalRowValues);
  subtotalRow.eachCell((cell) => {
    if (subtotalStyle) {
      cell.font = subtotalStyle.font;
      cell.fill = subtotalStyle.fill;
      cell.alignment = subtotalStyle.alignment;
      cell.border = subtotalStyle.border;
    }
  });

  // Format subtotal percentages and amounts
  allBranches.forEach((_, i) => {
    const baseCol = 2 + i * 4;
    const amountCols = [baseCol + 1, baseCol + 3];
    amountCols.forEach(c => subtotalRow.getCell(c).numFmt = amountFormat);
  });

  // ---- Column Widths ----
  sheet.columns.forEach((col, i) => {
    col.width = i === 0 ? 60 : 20;
  });

  // ---- Vertical hierarchy borders ----
  const lastRowNum = sheet.lastRow.number;
  // ---- Thin border for all cells (full grid) ----
  const lastRow = sheet.lastRow.number;
  const lastCol = sheet.columnCount;

  for (let r = 1; r <= lastRow; r++) {
    const row = sheet.getRow(r);
    for (let c = 1; c <= lastCol; c++) {
      const cell = row.getCell(c);

      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" }
      };
    }
  }


  let runningCol = 2;

  data.forEach(district => {
    let districtEnd = runningCol;

    district.loanSummaryWithDivisionalDtoList.forEach(divisional => {
      let divisionalEnd = runningCol;

      divisional.loanSummaryWithBranchDtoList.forEach(() => {
        const branchEnd = runningCol + 11;

        // 🟡 Branch border
        for (let r = 1; r <= lastRowNum; r++) {
          const cell = sheet.getRow(r).getCell(branchEnd);
          cell.border = {
            ...cell.border,
            right: { style: "thin", color: { argb: "FFFFFF00" } }
          };
        }

        runningCol += 12;
        divisionalEnd = runningCol - 1;
      });

      // 🔵 Divisional border
      for (let r = 1; r <= lastRowNum; r++) {
        const cell = sheet.getRow(r).getCell(divisionalEnd);
        cell.border = {
          ...cell.border,
          right: { style: "medium", color: { argb: "FF0000FF" } }
        };
      }

      districtEnd = divisionalEnd;
    });

    // 🔴 District border
    for (let r = 1; r <= lastRowNum; r++) {
      const cell = sheet.getRow(r).getCell(districtEnd);
      cell.border = {
        ...cell.border,
        right: { style: "thick", color: { argb: "FFFF0000" } }
      };
    }
  });


  // ---- Optional Protection ----
  if (loginPlaceId === "1") {
    protectSheetWithRandomPassword(sheet, 12);
  }

  // ---- Freeze Header ----
  sheet.views = [{ state: "frozen", xSplit: 1, ySplit: 6 }];

  // ---- Save File ----
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  saveAs(blob, `Loan Summary-${new Date().toISOString().split("T")[0]}.xlsx`);
};

export default LoanSummaryForDayExcel;
